import { create } from "zustand";
import { AgentScope } from "@/context/AgentScopeContext";

/**
 * Default chat sidebar / Flows panel width (px). Used both as the store's
 * initial value and as the reset target when navigating between pages so
 * each new page opens the panel at a predictable width regardless of any
 * resize the user did on the previous page.
 */
export const DEFAULT_PANEL_WIDTH = 400;

/**
 * Chat sidebar's allowed width range (px). Shared with MainLayout's
 * padding-right calculation and the conversation page's animation effect
 * so the chat panel, the reserved edge space, and the animation targets
 * all clamp identically.
 */
export const CHAT_MIN_WIDTH = 320;
export const CHAT_MAX_WIDTH = 500;
export const clampChatWidth = (w: number): number =>
  Math.min(CHAT_MAX_WIDTH, Math.max(CHAT_MIN_WIDTH, w));

interface AskFocalSidebarState {
  isOpen: boolean;
  threadKey: string | null;
  scope: AgentScope;
  /**
   * Drives the chat panel's transform and `<main>`'s padding-right. Flipped
   * to `true` one frame after `isOpen` becomes true (so the chat panel paints
   * off-screen before sliding in). On close it's set to `false` synchronously
   * with `isOpen` so the CSS transition runs symmetrically in both directions.
   */
  slideIn: boolean;
  /**
   * True while the user is dragging the chat sidebar's resize handle.
   * Consumed by MainLayout to disable the padding-right transition during
   * drag (otherwise the content edge would lag the cursor by 200ms).
   */
  isDragging: boolean;
  /**
   * Shared user-preferred width for the chat sidebar AND the Flows panel.
   * Written live during chat-handle drag (so MainLayout's padding-right
   * tracks the cursor) and on Flows-handle drag-end. Each panel clamps to
   * its own min/max range when reading.
   */
  panelWidth: number;
  open: (args: { threadKey: string; scope: AgentScope }) => void;
  close: () => void;
  toggle: (args: { threadKey: string; scope: AgentScope }) => void;
  setSlideIn: (slideIn: boolean) => void;
  setIsDragging: (isDragging: boolean) => void;
  setPanelWidth: (panelWidth: number) => void;
}

export const useAskFocalSidebarStore = create<AskFocalSidebarState>(
  (set, get) => ({
    isOpen: false,
    threadKey: null,
    scope: {},
    slideIn: false,
    isDragging: false,
    panelWidth: DEFAULT_PANEL_WIDTH,

    open: ({ threadKey, scope }) => {
      // `slideIn: false` so the chat panel paints at translateX(width) before
      // the consumer's rAF flips it to true → CSS transitions to translateX(0)
      // and main's padding-right transitions 0 → width simultaneously.
      set({ isOpen: true, threadKey, scope, slideIn: false });
    },

    close: () => {
      // Symmetric with open: flip both flags off in one render. Main's
      // padding-right transitions width → 0; chat panel's transform
      // transitions translateX(0) → translateX(width). Same duration, same
      // easing, same start frame.
      set({ isOpen: false, slideIn: false });
    },

    toggle: ({ threadKey, scope }) => {
      const state = get();
      if (state.isOpen) {
        get().close();
      } else {
        get().open({ threadKey, scope });
      }
    },

    setSlideIn: (slideIn) => set({ slideIn }),

    setIsDragging: (isDragging) => set({ isDragging }),

    setPanelWidth: (panelWidth) => set({ panelWidth }),
  }),
);
